import type { AgentId } from "./nav.js";

export type DetailFeature = {
	title: string;
	body: string;
	icon: "search" | "pause" | "shield" | "chart" | "link" | "bolt" | "message" | "key";
};

/** Desk persona by id, or a plain label (e.g. approval delivery). */
export type DetailAgent =
	| {
			agentId: AgentId;
			role: string;
	  }
	| {
			name: string;
			role: string;
	  };

export type IntegrationDetailContent = {
	id: string;
	tagline: string;
	summary: string;
	category: string;
	features: DetailFeature[];
	agents: DetailAgent[];
	permissions: string[];
	gates: string[];
	docs?: Array<{ label: string; href: string }>;
	notes?: string[];
	ctaLabel: string;
	ctaDisabled?: boolean;
};

const details: Record<string, IntegrationDetailContent> = {
	google_ads: {
		id: "google_ads",
		tagline: "Search campaigns with your approval on every dollar.",
		summary:
			"Connect a Google Ads account so Mairko can research keywords, draft paused campaigns, and propose budget or status changes. Nothing spends until you approve.",
		category: "Advertising",
		features: [
			{
				icon: "search",
				title: "Keyword research",
				body: "Ideas and search volumes from Google, grounded in your site and offer — not a third-party guess.",
			},
			{
				icon: "pause",
				title: "Paused campaign builds",
				body: "Budget, campaign, ad group, and ads are created paused. Safe to draft; turning spend on is a separate step you approve.",
			},
			{
				icon: "shield",
				title: "Spend needs your approval",
				body: "Budget increases and enable/pause changes wait for your yes — in Matrix or Slack. The first decision wins.",
			},
			{
				icon: "link",
				title: "Manager accounts welcome",
				body: "Connect a manager account, then pick the client account so agency setups work cleanly.",
			},
		],
		agents: [
			{ agentId: "paid_media", role: "Briefs, keywords, builds, budget, status" },
			{ agentId: "onboarding", role: "Suggests Ads after your company pack is ready" },
		],
		permissions: ["google_ads.read", "google_ads.write"],
		gates: [
			"Research & reports — automatic",
			"Create, raise budget, or enable — needs your approval",
			"Over your spend cap — blocked before it opens",
			"Timed-out requests are denied",
		],
		docs: [
			{ label: "Google Ads API access levels", href: "https://developers.google.com/google-ads/api/docs/productionize/access-levels" },
		],
		notes: [
			"Most campaign edits work at Explorer access; creating new client accounts under a manager needs Basic access.",
			"Test on paused campaigns before you enable real spend.",
		],
		ctaLabel: "Connect Google Ads",
	},
	ga4: {
		id: "ga4",
		tagline: "Real traffic and conversion context for every agent.",
		summary:
			"Connect a GA4 property so anomaly monitoring and paid briefs use real sessions — not guesses. Mairko never changes your Analytics settings.",
		category: "Analytics",
		features: [
			{
				icon: "chart",
				title: "Anomaly watch",
				body: "Scheduled checks on sessions, users, and views with clear, easy-to-read alerts.",
			},
			{
				icon: "bolt",
				title: "Shared context",
				body: "Onboarding and paid media pull the same property for briefs and sanity checks.",
			},
			{
				icon: "shield",
				title: "Read-only",
				body: "No edits to GA4 settings, audiences, or events from Mairko.",
			},
		],
		agents: [
			{ agentId: "ga4_anomaly", role: "Watches traffic for unusual swings" },
			{ agentId: "paid_media", role: "Performance context for briefs" },
			{ agentId: "onboarding", role: "Baseline traffic picture" },
		],
		permissions: ["ga4.read"],
		gates: ["Report pulls — automatic", "No write actions today"],
		ctaLabel: "Connect GA4",
	},
	gsc: {
		id: "gsc",
		tagline: "See how Search already understands your site.",
		summary:
			"Query and page data from Search Console help keyword and landing-page choices before you buy clicks.",
		category: "Analytics",
		features: [
			{
				icon: "search",
				title: "Top queries",
				body: "What people already type to find you — fuel for keyword research.",
			},
			{
				icon: "link",
				title: "Landing signals",
				body: "Pages that earn impressions become natural final-URL candidates for ads.",
			},
		],
		agents: [
			{ agentId: "onboarding", role: "Organic footprint in the company pack" },
			{ agentId: "paid_media", role: "Keyword & landing context" },
		],
		permissions: ["gsc.read"],
		gates: ["Read-only — automatic"],
		ctaLabel: "Connect Search Console",
	},
	gtm: {
		id: "gtm",
		tagline: "See which containers fire your conversion tags.",
		summary:
			"Connect a Tag Manager container so agents can audit tags today. Publishing changes later will need your approval.",
		category: "Analytics",
		features: [
			{
				icon: "bolt",
				title: "Pick a container",
				body: "Choose the GTM container that owns your site tags and conversion pixels.",
			},
			{
				icon: "shield",
				title: "Read-first",
				body: "Connect starts read-only. Write access is separate when you need edits.",
			},
		],
		agents: [
			{ agentId: "onboarding", role: "Tag & measurement context" },
			{ agentId: "cro", role: "Conversion plumbing checks" },
		],
		permissions: ["gtm.read"],
		gates: ["Container list — automatic", "Publishes — need your approval later"],
		ctaLabel: "Connect Tag Manager",
	},
	gbp: {
		id: "gbp",
		tagline: "Local listings and reviews in one place.",
		summary:
			"Connect a Business Profile location so local agents can review listing health and draft posts that wait for your approval.",
		category: "Local",
		features: [
			{
				icon: "link",
				title: "Pick a location",
				body: "One location per connection — the same simple pattern as picking a GA4 property.",
			},
			{
				icon: "shield",
				title: "Posts need your approval",
				body: "Listing reads run automatically; public posts wait for your yes.",
			},
		],
		agents: [
			{ agentId: "local", role: "Listing audit and review digest" },
			{ agentId: "onboarding", role: "Local presence in the company pack" },
		],
		permissions: ["gbp.read", "gbp.write"],
		gates: ["Location list — automatic", "Posts — need your approval when enabled"],
		ctaLabel: "Connect Business Profile",
	},
	google_drive: {
		id: "google_drive",
		tagline: "Brand files where your team already keeps them.",
		summary:
			"Connect Drive so Kai can list shared folders and file metadata for creatives — without downloading binaries yet. Credentials stay sealed.",
		category: "Creative assets",
		features: [
			{
				icon: "link",
				title: "Shared folders",
				body: "See logos, decks, and brand kits your team already stores in Drive.",
			},
			{
				icon: "shield",
				title: "Read-first",
				body: "Connect starts with file metadata only. Uploads and exports wait for a later approval path.",
			},
		],
		agents: [
			{ agentId: "creative", role: "Brand asset discovery" },
			{ agentId: "paid_media", role: "Ad creative references" },
			{ agentId: "content", role: "Brief attachments" },
		],
		permissions: ["google_drive.read"],
		gates: ["File list & metadata — automatic", "Uploads & exports — need your approval later"],
		ctaLabel: "Connect Google Drive",
	},
	google_photos: {
		id: "google_photos",
		tagline: "Product and brand photos in one library.",
		summary:
			"Connect Photos so Kai can list media metadata from your library. Full-resolution exports into Mairko come later behind approval.",
		category: "Creative assets",
		features: [
			{
				icon: "bolt",
				title: "Library scan",
				body: "Browse recent photos and albums your brand already shot.",
			},
			{
				icon: "shield",
				title: "Metadata only today",
				body: "Connect lists titles, dates, and ids — not bulk downloads yet.",
			},
		],
		agents: [
			{ agentId: "creative", role: "Photo library for briefs" },
			{ agentId: "content", role: "Visual references" },
		],
		permissions: ["google_photos.read"],
		gates: ["Media list — automatic", "Exports — need your approval later"],
		ctaLabel: "Connect Google Photos",
	},
	canva: {
		id: "canva",
		tagline: "Designs and brand kits your team already ships.",
		summary:
			"Connect Canva so Kai can list design metadata from your account. Creating or exporting designs later will need your approval.",
		category: "Creative assets",
		features: [
			{
				icon: "bolt",
				title: "Design inventory",
				body: "See Canva designs tied to your brand without leaving Matrix.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your Canva sign-in stays encrypted. Agents get only the access they need for the task.",
			},
		],
		agents: [
			{ agentId: "creative", role: "Design inventory" },
			{ agentId: "content", role: "Social and blog creatives" },
		],
		permissions: ["canva.read"],
		gates: ["Design list — automatic", "Create & export — need your approval later"],
		ctaLabel: "Connect Canva",
	},
	figma: {
		id: "figma",
		tagline: "Design files and brand systems in Figma.",
		summary:
			"Connect Figma so Kai can list team files for creative briefs. Edits and publishes wait for your approval later.",
		category: "Creative assets",
		features: [
			{
				icon: "link",
				title: "Team files",
				body: "List projects and files from a Figma team you already use.",
			},
			{
				icon: "shield",
				title: "Read-first",
				body: "Connect starts with file metadata. Writes stay behind approval.",
			},
		],
		agents: [
			{ agentId: "creative", role: "Design system & file discovery" },
			{ agentId: "developer", role: "Design-to-build context" },
		],
		permissions: ["figma.read"],
		gates: ["File list — automatic", "Edits — need your approval later"],
		ctaLabel: "Connect Figma",
	},
	dropbox: {
		id: "dropbox",
		tagline: "Shared folders for creatives and brand kits.",
		summary:
			"Connect Dropbox so Kai can list folder contents for brand assets. Uploads and moves come later behind approval.",
		category: "Creative assets",
		features: [
			{
				icon: "link",
				title: "Folder listing",
				body: "Browse shared Dropbox paths your creative team already uses.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your Dropbox sign-in stays encrypted. Agents never see raw tokens.",
			},
		],
		agents: [
			{ agentId: "creative", role: "Shared asset folders" },
			{ agentId: "paid_media", role: "Ad creative packs" },
		],
		permissions: ["dropbox.read"],
		gates: ["Folder list — automatic", "Uploads & moves — need your approval later"],
		ctaLabel: "Connect Dropbox",
	},
	github: {
		id: "github",
		tagline: "Connect the repo that powers your site.",
		summary:
			"Connect GitHub so Mairko can read your site source of truth — branches and files today, pull requests later with your approval. Credentials stay sealed.",
		category: "Hosting",
		features: [
			{
				icon: "link",
				title: "Pick a repository",
				body: "Choose one repository per connection. Agents work only with that repo — not whatever name they invent.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your sign-in stays encrypted. Agents get only the access they need for the task.",
			},
			{
				icon: "bolt",
				title: "Works with your host",
				body: "Pairs with Vercel, Cloudflare, and GoDaddy as you connect them for site work.",
			},
		],
		agents: [
			{ agentId: "developer", role: "Site source and publish path" },
			{ agentId: "onboarding", role: "Detect existing site repos" },
		],
		permissions: ["github.read"],
		gates: ["Repo list — automatic", "Writes & pull requests — need your approval later"],
		ctaLabel: "Connect GitHub",
	},
	vercel: {
		id: "vercel",
		tagline: "Connect the project that hosts your site.",
		summary:
			"Connect Vercel so Mairko can list projects today and run preview or production deploys later — always with your approval. Credentials stay sealed.",
		category: "Hosting",
		features: [
			{
				icon: "link",
				title: "Pick a project",
				body: "Choose one Vercel project per connection. Agents work only with that project.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your Vercel sign-in stays encrypted. Agents get only the access they need.",
			},
			{
				icon: "bolt",
				title: "Deploys need your approval",
				body: "Production promotes wait for your yes before anything goes live.",
			},
		],
		agents: [
			{ agentId: "developer", role: "Deploy after approval" },
			{ agentId: "onboarding", role: "Detect existing Vercel projects" },
		],
		permissions: ["vercel.read"],
		gates: ["Project list — automatic", "Deploy or promote — need your approval later"],
		ctaLabel: "Connect Vercel",
	},
	cloudflare: {
		id: "cloudflare",
		tagline: "Connect the zone that owns your domain.",
		summary:
			"Connect Cloudflare so Mairko can list zones today and propose DNS changes later — always with your approval. Credentials stay sealed.",
		category: "Hosting",
		features: [
			{
				icon: "link",
				title: "Pick a zone",
				body: "Choose one zone per connection. Agents work only with that domain setup.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your sign-in stays encrypted. Agents get only the access they need.",
			},
			{
				icon: "bolt",
				title: "DNS changes need your approval",
				body: "Record updates wait for your yes before anything changes in production.",
			},
		],
		agents: [
			{ agentId: "developer", role: "DNS and edge config" },
			{ agentId: "onboarding", role: "Detect existing zones" },
		],
		permissions: ["cloudflare.read"],
		gates: ["Zone list — automatic", "DNS changes — need your approval later"],
		ctaLabel: "Connect Cloudflare",
	},
	godaddy: {
		id: "godaddy",
		tagline: "Connect the domain you already own.",
		summary:
			"Paste a GoDaddy Personal Access Token so Mairko can list and pick domains. Your token stays encrypted at rest.",
		category: "Hosting",
		features: [
			{
				icon: "link",
				title: "Pick a domain",
				body: "Choose one primary domain per connection. Agents work only with that domain.",
			},
			{
				icon: "shield",
				title: "Personal Access Token",
				body: "GoDaddy uses a Personal Access Token instead of a full sign-in. Use domain-read access only; tokens stay encrypted.",
			},
			{
				icon: "bolt",
				title: "Transfers need extra care",
				body: "Anything that moves or cancels a domain will require a stronger confirmation.",
			},
		],
		agents: [
			{ agentId: "developer", role: "Domain ownership checks" },
			{ agentId: "onboarding", role: "Detect existing domains" },
		],
		permissions: ["godaddy.read"],
		gates: ["Domain list — automatic", "Transfers or cancel — need your approval later"],
		ctaLabel: "Connect GoDaddy",
	},
	linkedin: {
		id: "linkedin",
		tagline: "Connect your Company Page for organic work.",
		summary:
			"Connect LinkedIn Community Management so content agents can see your Company Page. Advertising uses a separate LinkedIn Ads connection — LinkedIn requires two apps for organic and paid.",
		category: "Advertising",
		features: [
			{
				icon: "link",
				title: "Pick a Company Page",
				body: "Choose one Company Page per connection. Agents work only with that Page.",
			},
			{
				icon: "shield",
				title: "Ads is a separate connect",
				body: "Paid media lives under LinkedIn Ads — connect that product when you are ready to spend.",
			},
			{
				icon: "bolt",
				title: "Posts need your approval",
				body: "Organic publish will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "content", role: "Company Page context" },
			{ agentId: "onboarding", role: "Detect existing Pages" },
		],
		permissions: ["linkedin.read"],
		gates: ["Page list — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect LinkedIn",
	},
	linkedin_ads: {
		id: "linkedin_ads",
		tagline: "Connect your Ad Account for B2B paid.",
		summary:
			"Connect the LinkedIn Advertising app (separate from Community Management). Pick an ad account — spend always needs your approval.",
		category: "Advertising",
		features: [
			{
				icon: "link",
				title: "Pick an ad account",
				body: "Choose one ad account per connection — same simple pattern as Google Ads.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Read access only for now. Your sign-in stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Spend needs your approval",
				body: "Campaign creates and budget changes will wait for your yes.",
			},
		],
		agents: [{ agentId: "paid_media", role: "LinkedIn Ads performance & planning" }],
		permissions: ["linkedin_ads.read"],
		gates: ["Ad account list — automatic", "Spend changes — need your approval later"],
		ctaLabel: "Connect LinkedIn Ads",
	},
	x: {
		id: "x",
		tagline: "Connect the brand X account for organic work.",
		summary:
			"Sign in with X as the brand handle — not a personal alt. That account becomes the one Mairko uses. X Ads will be a separate product later.",
		category: "Social",
		features: [
			{
				icon: "link",
				title: "Confirm the account",
				body: "We show the signed-in @handle so you can confirm the right brand account before agents use it.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Read access first. Your sign-in stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Posts need your approval",
				body: "Publishing will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "social", role: "Organic posts and mentions" },
			{ agentId: "content", role: "Draft context" },
		],
		permissions: ["x.read"],
		gates: ["Account confirm — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect X",
	},
	shopify: {
		id: "shopify",
		tagline: "Connect your Shopify store for catalog context.",
		summary:
			"Enter your *.myshopify.com domain, then install Mairko’s Shopify app. Your store is connected automatically. Store access stays encrypted.",
		category: "Commerce",
		features: [
			{
				icon: "link",
				title: "Connect your shop",
				body: "Your shop domain is captured at Connect and becomes the store agents use.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Product read access first. Your store token stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Catalog for briefs",
				body: "Product reads feed research and content. Catalog writes wait for your approval later.",
			},
		],
		agents: [
			{ agentId: "content", role: "Product-aware drafts" },
			{ agentId: "developer", role: "Storefront & catalog context" },
			{ agentId: "onboarding", role: "Detect existing store" },
		],
		permissions: ["shopify.read"],
		gates: ["Shop metadata — automatic", "Catalog writes — need your approval later"],
		ctaLabel: "Connect Shopify",
	},
	wordpress: {
		id: "wordpress",
		tagline: "Connect a WordPress.com or Jetpack site.",
		summary:
			"Sign in with WordPress.com. Works for WordPress.com blogs and self-hosted sites with Jetpack. Pick the site after consent — credentials stay sealed.",
		category: "CMS",
		features: [
			{
				icon: "link",
				title: "Pick a site",
				body: "We list sites from your grant; choose one for this connection.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Your WordPress.com sign-in stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Publish needs your approval",
				body: "Post and page creates will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "content", role: "Draft → publish pipeline" },
			{ agentId: "developer", role: "Site content" },
			{ agentId: "seo", role: "Page shipping" },
		],
		permissions: ["wordpress.read"],
		gates: ["Site list — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect WordPress",
	},
	wix: {
		id: "wix",
		tagline: "Install Mairko on a Wix site.",
		summary:
			"Install the Mairko Wix app — your site connects automatically. Access stays sealed.",
		category: "CMS",
		features: [
			{
				icon: "link",
				title: "One install, one site",
				body: "Installing Mairko on a Wix site connects that site automatically.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Install credentials stay encrypted; access is refreshed as needed.",
			},
			{
				icon: "bolt",
				title: "Publish needs your approval",
				body: "CMS and blog writes will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "content", role: "Draft → publish pipeline" },
			{ agentId: "developer", role: "Site content" },
			{ agentId: "seo", role: "Page shipping" },
		],
		permissions: ["wix.read"],
		gates: ["Connection check — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect Wix",
	},
	webflow: {
		id: "webflow",
		tagline: "Connect a Webflow site for CMS work.",
		summary:
			"Sign in with Webflow, list authorized sites, and pick one. CMS and pages access stays sealed.",
		category: "CMS",
		features: [
			{
				icon: "link",
				title: "Pick a site",
				body: "After you sign in, choose one Webflow site for this connection.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Site, CMS, and pages read access on first Connect. Your sign-in stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Publish needs your approval",
				body: "CMS item writes will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "content", role: "Draft → CMS publish" },
			{ agentId: "developer", role: "Site content" },
			{ agentId: "seo", role: "Page shipping" },
		],
		permissions: ["webflow.read"],
		gates: ["Site list — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect Webflow",
	},
	squarespace: {
		id: "squarespace",
		tagline: "Connect a Squarespace Commerce site.",
		summary:
			"Squarespace’s public API focuses on Commerce (not blog or CMS pages). Sign in once, your website connects automatically, then catalog tools can run with your approval on writes.",
		category: "CMS",
		features: [
			{
				icon: "link",
				title: "Website connects automatically",
				body: "One sign-in covers one site. We connect that website for you after consent.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Access refreshes on a short cycle and stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Commerce first",
				body: "Products and inventory reads now; catalog writes wait for your approval later.",
			},
		],
		agents: [
			{ agentId: "content", role: "Catalog context" },
			{ agentId: "developer", role: "Site metadata" },
		],
		permissions: ["squarespace.read"],
		gates: ["Website check — automatic", "Catalog writes — need your approval later"],
		ctaLabel: "Connect Squarespace",
	},
	pinterest: {
		id: "pinterest",
		tagline: "Connect the Pinterest account for organic work.",
		summary:
			"Sign in with Pinterest as the brand account. We confirm the username before agents use it. Ads access can be added later.",
		category: "Social",
		features: [
			{
				icon: "link",
				title: "Confirm the account",
				body: "We show the signed-in username so you can confirm the right brand account.",
			},
			{
				icon: "shield",
				title: "Credentials stay sealed",
				body: "Read access first for account, boards, and pins. Your sign-in stays encrypted.",
			},
			{
				icon: "bolt",
				title: "Pins need your approval",
				body: "Creating pins will wait for your yes.",
			},
		],
		agents: [
			{ agentId: "social", role: "Organic pins and boards" },
			{ agentId: "content", role: "Draft context" },
		],
		permissions: ["pinterest.read"],
		gates: ["Account confirm — automatic", "Publish — needs your approval later"],
		ctaLabel: "Connect Pinterest",
	},
	meta_ads: {
		id: "meta_ads",
		tagline: "Facebook & Instagram ads with the same approvals as Search.",
		summary:
			"Meta Ads is coming soon. You’ll connect an ad account, draft paused campaigns, and approve every spend or public change — just like Google Ads.",
		category: "Advertising",
		features: [
			{
				icon: "pause",
				title: "Paused creates",
				body: "Build inactive campaigns before any spend goes live.",
			},
			{
				icon: "shield",
				title: "Spend needs your approval",
				body: "Every spend or public change waits for your yes.",
			},
		],
		agents: [{ agentId: "paid_media", role: "Display & social paid media" }],
		permissions: ["Meta ads_management (App Review)"],
		gates: ["Same approval rules as Google Ads once available"],
		notes: ["Coming soon — Google Search is available first."],
		ctaLabel: "Coming soon",
		ctaDisabled: true,
	},
	slack: {
		id: "slack",
		tagline: "Approval cards where your team already works.",
		summary:
			"Slack delivers Approve / Reject for actions that need your say. Approvals stay in sync across Matrix and Slack so the decision is always consistent.",
		category: "Messaging",
		features: [
			{
				icon: "message",
				title: "Rich approval cards",
				body: "One-tap decisions with a clear summary and cost — updated in place when someone decides elsewhere.",
			},
			{
				icon: "bolt",
				title: "First decision wins",
				body: "Approve in Matrix or Slack — the other channel updates to match automatically.",
			},
			{
				icon: "key",
				title: "Verified identity",
				body: "Your Slack member is verified when you connect — never guessed from a message alone.",
			},
		],
		agents: [
			{ name: "All agents that need approval", role: "Sends approval requests to Slack" },
		],
		permissions: ["chat:write", "interactive components"],
		gates: [
			"Buttons send a clear Approve or Reject",
			"Never approve from free-text chat alone",
			"Timed-out requests are denied",
		],
		ctaLabel: "Connect Slack",
	},
	whatsapp: {
		id: "whatsapp",
		tagline: "Approvals on the client’s phone — coming soon.",
		summary:
			"Coming soon. Designed so the client starts the conversation. Inside the 24-hour window, replies are free; outside it, a short template plus a secure link to confirm.",
		category: "Messaging",
		features: [
			{
				icon: "message",
				title: "In-window replies",
				body: "Free replies while the customer service window is open.",
			},
			{
				icon: "link",
				title: "Template + secure link",
				body: "When the window is closed, a short approved message plus a signed link to confirm.",
			},
		],
		agents: [{ name: "Approval delivery", role: "Approvals to the client’s phone" }],
		permissions: ["WhatsApp Business API", "Approved templates"],
		gates: ["Opening a link never approves by itself", "Identity verified when you connect"],
		ctaLabel: "Coming soon",
		ctaDisabled: true,
	},
	email: {
		id: "email",
		tagline: "Gmail for approval delivery and confirm links.",
		summary:
			"Connect Gmail with the same Google sign-in you use for Analytics and Ads. Approvals use signed links that open a confirm page — opening the email alone never approves anything.",
		category: "Messaging",
		features: [
			{
				icon: "shield",
				title: "Confirm to decide",
				body: "Opening or previewing a link never changes an approval. You confirm on a page that submits your decision.",
			},
			{
				icon: "key",
				title: "Per-recipient links",
				body: "Forwarding an email does not grant the forwarder’s rights.",
			},
			{
				icon: "link",
				title: "Credentials stay sealed",
				body: "Read and send access for Gmail. Your sign-in stays encrypted.",
			},
		],
		agents: [{ name: "Approval delivery", role: "Approvers who live in email" }],
		permissions: ["gmail.read", "gmail.write"],
		gates: ["Opening a link never approves", "Timed-out requests are denied"],
		ctaLabel: "Connect Gmail",
	},
	teams: {
		id: "teams",
		tagline: "Approval cards in Microsoft Teams — coming soon.",
		summary:
			"Coming soon. Approve or reject from Adaptive Cards in Teams, with the same consistent decisions as Matrix and Slack.",
		category: "Messaging",
		features: [
			{
				icon: "message",
				title: "Approval cards",
				body: "Adaptive Cards with Approve / Reject, kept in sync with Matrix.",
			},
		],
		agents: [{ name: "Approval delivery", role: "Teams-native decisions" }],
		permissions: ["Teams bot"],
		gates: ["Decisions stay consistent across channels"],
		ctaLabel: "Coming soon",
		ctaDisabled: true,
	},
};

export function getIntegrationDetail(
	id: string,
): IntegrationDetailContent | null {
	return details[id] ?? null;
}
